import { useQuery } from "@tanstack/react-query";
import { fetchStats, fetchEngagement, fetchLearning } from "../lib/api";

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color ?? "text-gray-900"}`}>{value}</p>
    </div>
  );
}

export function DashboardPage() {
  const stats = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  const engagement = useQuery({ queryKey: ["engagement"], queryFn: () => fetchEngagement("30d") });
  const learning = useQuery({ queryKey: ["learning"], queryFn: () => fetchLearning("30d") });

  const s = stats.data?.data;
  const e = engagement.data?.data;
  const l = learning.data?.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      {stats.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Total Users" value={s?.totalUsers ?? 0} />
            <StatCard label="Active Subscriptions" value={s?.activeSubscriptions ?? 0} color="text-green-600" />
            <StatCard label="Trial Users" value={s?.trialUsers ?? 0} color="text-blue-600" />
            <StatCard label="Free Users" value={s?.freeUsers ?? 0} color="text-purple-600" />
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="MRR" value={`$${(s?.mrr ?? 0).toFixed(2)}`} color="text-green-700" />
            <StatCard label="Conversion Rate" value={`${s?.conversionRate ?? 0}%`} />
            <StatCard label="Expired Trials" value={s?.expiredTrials ?? 0} color="text-amber-600" />
            <StatCard label="Cancelled" value={s?.cancelledUsers ?? 0} color="text-red-500" />
            <StatCard label="Abandoned Carts" value={s?.abandonedCarts ?? 0} color="text-orange-500" />
          </div>
          <StatCard label="Total Game Sessions" value={s?.totalGameSessions ?? 0} />
        </>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="mb-3 text-lg font-semibold">Engagement (30 days)</h2>
          {engagement.isLoading ? (
            <p className="text-gray-500">Loading…</p>
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Total Sessions</dt>
                <dd className="font-medium">{e?.totalSessions ?? 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Active Children</dt>
                <dd className="font-medium">{e?.activeChildren ?? 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Avg Session Time</dt>
                <dd className="font-medium">{Math.round((e?.avgSessionTimeSeconds ?? 0) / 60)} min</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Total Play Time</dt>
                <dd className="font-medium">{e?.totalPlayTimeMinutes ?? 0} min</dd>
              </div>
            </dl>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="mb-3 text-lg font-semibold">Learning (30 days)</h2>
          {learning.isLoading ? (
            <p className="text-gray-500">Loading…</p>
          ) : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Completions</dt>
                <dd className="font-medium">{l?.totalCompletions ?? 0}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">3-Star Rate</dt>
                <dd className="font-medium">{l?.threeStarRate ?? 0}%</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Avg Accuracy</dt>
                <dd className="font-medium">{l?.avgAccuracy ?? 0}%</dd>
              </div>
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
